/**
 * peopleHubActivitySlice.ts
 *
 * Redux state for the in-flight activity forms that the user fills while a
 * session is active (Pulse Check, Full Observation, Routine Observation).
 *
 * Design rules:
 *  • One "draft" object per activity type, each independently resettable.
 *  • The draft shape mirrors the types in beginActivity.common.types.ts so
 *    the common components (BalancedFeedback, CoachingHistory, NewAction,
 *    FinalSignatures) can be driven directly from Redux state.
 *  • No derivation (canComplete, validationError) lives here — those stay
 *    as computed values in the component hooks (usePulseCheck, etc.).
 *  • Full Observation and Routine Observation share the same draft shape
 *    because they have the same four-screen structure.
 *
 * Pattern: identical to pulseCheckSlice.ts / newHireOnboardingSlice.ts.
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createApiClient, getLocalTimestamp, getTimezoneOffsetString } from "../../api/apiClient";
import {
  startActivity as startActivityApi,
  discardActivity as discardActivityApi,
  resumeActivity as resumeActivityApi,
  StartActivityResponse,
  StartActivityAssigneeOwner,
} from "../../screens/PeopleHub/peopleHubApi";
import type { ActivitySession } from "../../screens/PeopleHub/BeginActivity/PeopleActivityScreen";
import type {
  BalancedFeedbackData,
  CoachingHistoryItem,
  NewActionDraft,
  SignatureData,
  FollowUpResult,
  WeeklyMetricRow,
  ActivityType,
} from "../../screens/PeopleHub/BeginActivity/common/types/beginActivity.common.types";

// ─── Save / submit API response ───────────────────────────────────────────────
export interface ActivitySaveResponse {
  activityId: string;
  status: "draft" | "submitted";
  savedAt: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Pulse Check draft
// ─────────────────────────────────────────────────────────────────────────────
export interface PulseCheckDraft {
  /** ISO timestamp when the draft was last auto-saved. */
  savedAt: string | null;
  /** Existing server-side activity ID if resuming a draft. */
  activityId: string | null;
  metricRows: WeeklyMetricRow[];
  feedback: BalancedFeedbackData;
  openItems: CoachingHistoryItem[];
  historyItems: CoachingHistoryItem[];
  actionDraft: NewActionDraft;
  signatures: SignatureData;
}

const pulseCheckDraftInitial: PulseCheckDraft = {
  savedAt: null,
  activityId: null,
  metricRows: [],
  feedback: { good: "", improve: "", keep: "", support: "" },
  openItems: [],
  historyItems: [],
  actionDraft: { type: "", focus: "", otherFocus: "", opportunity: "", plan: "" },
  signatures: { employeeSignature: undefined, managerSignature: undefined },
};

// ─────────────────────────────────────────────────────────────────────────────
// Full Observation / Routine Observation draft
// (Both types share the same 4-screen structure.)
// ─────────────────────────────────────────────────────────────────────────────

/** Represents one question-response pair on a BuildingStatus screen. */
export interface BuildingStatusAnswer {
  questionId: string;
  questionText: string;
  answer: string | boolean | null;
  notes: string;
}

/** One of the three activity screens (Before / During / After). */
export interface ObservationScreenDraft {
  metricRows: WeeklyMetricRow[];
  buildingStatusAnswers: BuildingStatusAnswer[];
  notes: string;
}

export interface ObservationActivityDraft {
  savedAt: string | null;
  activityId: string | null;
  /** "before_activity" | "during_activity" | "after_activity" | "coaching" */
  activeScreen: "before_activity" | "during_activity" | "after_activity" | "coaching";
  before: ObservationScreenDraft;
  during: ObservationScreenDraft;
  after: ObservationScreenDraft;
  // Coaching screen (shared components)
  feedback: BalancedFeedbackData;
  openItems: CoachingHistoryItem[];
  historyItems: CoachingHistoryItem[];
  actionDraft: NewActionDraft;
  signatures: SignatureData;
}

const observationScreenInitial: ObservationScreenDraft = {
  metricRows: [],
  buildingStatusAnswers: [],
  notes: "",
};

const observationDraftInitial: ObservationActivityDraft = {
  savedAt: null,
  activityId: null,
  activeScreen: "before_activity",
  before: { ...observationScreenInitial },
  during: { ...observationScreenInitial },
  after: { ...observationScreenInitial },
  feedback: { good: "", improve: "", keep: "", support: "" },
  openItems: [],
  historyItems: [],
  actionDraft: { type: "", focus: "", otherFocus: "", opportunity: "", plan: "" },
  signatures: { employeeSignature: undefined, managerSignature: undefined },
};

// ─────────────────────────────────────────────────────────────────────────────
// Root state
// ─────────────────────────────────────────────────────────────────────────────
export interface PeopleHubActivityState {
  pulseCheck: PulseCheckDraft;
  fullObservation: ObservationActivityDraft;
  routineObservation: ObservationActivityDraft;
  /** Shared loading flag for save-draft / submit calls. */
  isSaving: boolean;
  /** Shared loading flag for fetch-draft (resume) calls. */
  isLoadingDraft: boolean;
  /** Last save/submit error. */
  saveError: string | null;
  /** Set when the activity has been successfully submitted. */
  successActivityId: string | null;
  /** Response captured from POST /people/activities/start for the in-progress session. */
  currentActivity: StartActivityResponse | null;
  isStartingActivity: boolean;
  startActivityError: string | null;
  /** Loading flag for POST /people/activities RESUME (clicking a queue row to resume). */
  isResumingActivity: boolean;
  resumeActivityError: string | null;
  /** Full session (employee details + start-activity response) for the screen currently
   *  being navigated to — read by PulseCheck/Observation screens instead of route params. */
  activeSession: ActivitySession | null;
}

const initialState: PeopleHubActivityState = {
  pulseCheck: pulseCheckDraftInitial,
  fullObservation: observationDraftInitial,
  routineObservation: observationDraftInitial,
  isSaving: false,
  isLoadingDraft: false,
  saveError: null,
  successActivityId: null,
  currentActivity: null,
  isStartingActivity: false,
  startActivityError: null,
  isResumingActivity: false,
  resumeActivityError: null,
  activeSession: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// Async thunks
// ─────────────────────────────────────────────────────────────────────────────

/** Fetch 12-week metric rows for a given employee + activity type. */
export const fetchMetricRows = createAsyncThunk(
  "peopleHubActivity/fetchMetricRows",
  async (
    payload: { employeeId: string; activityType: ActivityType },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const res = await api.get<WeeklyMetricRow[]>("/people-hub/metrics", {
        params: { employeeId: payload.employeeId, activityType: payload.activityType },
      });
      return { activityType: payload.activityType, rows: res.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to fetch metrics"
      );
    }
  }
);

/** Fetch open coaching items + history for a given employee. */
export const fetchCoachingItems = createAsyncThunk(
  "peopleHubActivity/fetchCoachingItems",
  async (
    payload: { employeeId: string; activityType: ActivityType },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const [openRes, histRes] = await Promise.all([
        api.get<CoachingHistoryItem[]>("/people-hub/coaching-items/open", {
          params: { employeeId: payload.employeeId },
        }),
        api.get<CoachingHistoryItem[]>("/people-hub/coaching-items/history", {
          params: { employeeId: payload.employeeId },
        }),
      ]);
      return {
        activityType: payload.activityType,
        openItems: openRes.data,
        historyItems: histRes.data,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to fetch coaching items"
      );
    }
  }
);

/** Start a new Pulse Check / Observation activity — POST /people/activities/start. */
export const startPeopleHubActivity = createAsyncThunk(
  "peopleHubActivity/startPeopleHubActivity",
  async (
    payload: StartActivityAssigneeOwner & {
      activityType: "OBSERVATION" | "PULSE_CHECK";
      observationType?: "Full" | "Routine";
      employeeId: string | number;
      locationId: string | number;
      locationName?: string;
      locationSapId?: string;
      role?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await startActivityApi({
        ...payload,
        clientTimestamp: getLocalTimestamp(),
        clientTimezone: getTimezoneOffsetString(),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to start activity"
      );
    }
  }
);

/** Discard an activity started but never saved/completed — DELETE /people/activities/{id}.
 *  Call from the Exit/Leave-without-saving path before navigating away. */
export const discardPeopleHubActivity = createAsyncThunk(
  "peopleHubActivity/discardPeopleHubActivity",
  async (activityId: string | number, { rejectWithValue }) => {
    try {
      await discardActivityApi(activityId);
      return activityId;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to discard activity"
      );
    }
  }
);

/** Resume an in-progress activity from a My Queue row — POST /people/activities (action: RESUME).
 *  Parses the checklistSelections JSON string so components can map it by itemId. */
export const resumePeopleHubActivity = createAsyncThunk(
  "peopleHubActivity/resumePeopleHubActivity",
  async (
    payload: {
      taskId?: string | number;
      activityId?: string | number;
      activityType: "OBSERVATION" | "PULSE_CHECK";
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await resumeActivityApi(payload.taskId, payload.activityType, payload.activityId);
      const checklistSelections =
        typeof response.checklistSelections === "string"
          ? JSON.parse(response.checklistSelections)
          : response.checklistSelections ?? {};
      const customChecklist =
        typeof response.customChecklist === "string"
          ? JSON.parse(response.customChecklist)
          : response.customChecklist ?? [];
      return { ...response, checklistSelections, customChecklist };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to resume activity"
      );
    }
  }
);

/** Save the current draft to the server. */
export const saveDraft = createAsyncThunk(
  "peopleHubActivity/saveDraft",
  async (
    payload: {
      activityType: ActivityType;
      sessionId: string;
      employeeId: string;
      draft: PulseCheckDraft | ObservationActivityDraft;
    },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const res = await api.post<ActivitySaveResponse>("/people-hub/activities/draft", {
        activityType: payload.activityType,
        sessionId: payload.sessionId,
        employeeId: payload.employeeId,
        draft: payload.draft,
      });
      return { activityType: payload.activityType, response: res.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to save draft"
      );
    }
  }
);

/** Submit a completed activity. */
export const submitActivity = createAsyncThunk(
  "peopleHubActivity/submitActivity",
  async (
    payload: {
      activityType: ActivityType;
      sessionId: string;
      employeeId: string;
      draft: PulseCheckDraft | ObservationActivityDraft;
    },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();
      const res = await api.post<ActivitySaveResponse>("/people-hub/activities/submit", {
        activityType: payload.activityType,
        sessionId: payload.sessionId,
        employeeId: payload.employeeId,
        draft: payload.draft,
      });
      return { activityType: payload.activityType, response: res.data };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ?? error?.message ?? "Failed to submit activity"
      );
    }
  }
);

// ─────────────────────────────────────────────────────────────────────────────
// Slice
// ─────────────────────────────────────────────────────────────────────────────
const peopleHubActivitySlice = createSlice({
  name: "peopleHubActivity",
  initialState,
  reducers: {
    // ── Pulse Check ───────────────────────────────────────────────────────
    setPulseCheckFeedback(state, action: PayloadAction<BalancedFeedbackData>) {
      state.pulseCheck.feedback = action.payload;
    },
    setPulseCheckActionDraft(state, action: PayloadAction<NewActionDraft>) {
      state.pulseCheck.actionDraft = action.payload;
    },
    setPulseCheckSignatures(state, action: PayloadAction<SignatureData>) {
      state.pulseCheck.signatures = action.payload;
    },
    setPulseCheckOpenItemResult(
      state,
      action: PayloadAction<{ id: string; result: FollowUpResult }>
    ) {
      const item = state.pulseCheck.openItems.find((i) => i.id === action.payload.id);
      if (item) {
        item.followUpResult = action.payload.result;
        item.followUpFeedback = "";
      }
    },
    setPulseCheckOpenItemFeedback(
      state,
      action: PayloadAction<{ id: string; feedback: string }>
    ) {
      const item = state.pulseCheck.openItems.find((i) => i.id === action.payload.id);
      if (item) {
        item.followUpFeedback = action.payload.feedback;
      }
    },
    resetPulseCheckDraft(state) {
      state.pulseCheck = { ...pulseCheckDraftInitial };
    },

    // ── Full Observation ──────────────────────────────────────────────────
    setFullObsActiveScreen(
      state,
      action: PayloadAction<ObservationActivityDraft["activeScreen"]>
    ) {
      state.fullObservation.activeScreen = action.payload;
    },
    setFullObsBuildingStatusAnswer(
      state,
      action: PayloadAction<{
        screen: "before" | "during" | "after";
        answer: BuildingStatusAnswer;
      }>
    ) {
      const { screen, answer } = action.payload;
      const screenDraft = state.fullObservation[screen];
      const idx = screenDraft.buildingStatusAnswers.findIndex(
        (a) => a.questionId === answer.questionId
      );
      if (idx >= 0) {
        screenDraft.buildingStatusAnswers[idx] = answer;
      } else {
        screenDraft.buildingStatusAnswers.push(answer);
      }
    },
    setFullObsNotes(
      state,
      action: PayloadAction<{ screen: "before" | "during" | "after"; notes: string }>
    ) {
      state.fullObservation[action.payload.screen].notes = action.payload.notes;
    },
    setFullObsFeedback(state, action: PayloadAction<BalancedFeedbackData>) {
      state.fullObservation.feedback = action.payload;
    },
    setFullObsActionDraft(state, action: PayloadAction<NewActionDraft>) {
      state.fullObservation.actionDraft = action.payload;
    },
    setFullObsSignatures(state, action: PayloadAction<SignatureData>) {
      state.fullObservation.signatures = action.payload;
    },
    setFullObsOpenItemResult(
      state,
      action: PayloadAction<{ id: string; result: FollowUpResult }>
    ) {
      const item = state.fullObservation.openItems.find((i) => i.id === action.payload.id);
      if (item) {
        item.followUpResult = action.payload.result;
        item.followUpFeedback = "";
      }
    },
    setFullObsOpenItemFeedback(
      state,
      action: PayloadAction<{ id: string; feedback: string }>
    ) {
      const item = state.fullObservation.openItems.find((i) => i.id === action.payload.id);
      if (item) {
        item.followUpFeedback = action.payload.feedback;
      }
    },
    resetFullObservationDraft(state) {
      state.fullObservation = { ...observationDraftInitial };
    },

    // ── Routine Observation ───────────────────────────────────────────────
    setRoutineObsActiveScreen(
      state,
      action: PayloadAction<ObservationActivityDraft["activeScreen"]>
    ) {
      state.routineObservation.activeScreen = action.payload;
    },
    setRoutineObsBuildingStatusAnswer(
      state,
      action: PayloadAction<{
        screen: "before" | "during" | "after";
        answer: BuildingStatusAnswer;
      }>
    ) {
      const { screen, answer } = action.payload;
      const screenDraft = state.routineObservation[screen];
      const idx = screenDraft.buildingStatusAnswers.findIndex(
        (a) => a.questionId === answer.questionId
      );
      if (idx >= 0) {
        screenDraft.buildingStatusAnswers[idx] = answer;
      } else {
        screenDraft.buildingStatusAnswers.push(answer);
      }
    },
    setRoutineObsNotes(
      state,
      action: PayloadAction<{ screen: "before" | "during" | "after"; notes: string }>
    ) {
      state.routineObservation[action.payload.screen].notes = action.payload.notes;
    },
    setRoutineObsFeedback(state, action: PayloadAction<BalancedFeedbackData>) {
      state.routineObservation.feedback = action.payload;
    },
    setRoutineObsActionDraft(state, action: PayloadAction<NewActionDraft>) {
      state.routineObservation.actionDraft = action.payload;
    },
    setRoutineObsSignatures(state, action: PayloadAction<SignatureData>) {
      state.routineObservation.signatures = action.payload;
    },
    setRoutineObsOpenItemResult(
      state,
      action: PayloadAction<{ id: string; result: FollowUpResult }>
    ) {
      const item = state.routineObservation.openItems.find((i) => i.id === action.payload.id);
      if (item) {
        item.followUpResult = action.payload.result;
        item.followUpFeedback = "";
      }
    },
    setRoutineObsOpenItemFeedback(
      state,
      action: PayloadAction<{ id: string; feedback: string }>
    ) {
      const item = state.routineObservation.openItems.find((i) => i.id === action.payload.id);
      if (item) {
        item.followUpFeedback = action.payload.feedback;
      }
    },
    resetRoutineObservationDraft(state) {
      state.routineObservation = { ...observationDraftInitial };
    },

    // ── Error / success ───────────────────────────────────────────────────
    clearSaveError(state) {
      state.saveError = null;
    },
    clearSuccessActivityId(state) {
      state.successActivityId = null;
    },
    clearCurrentActivity(state) {
      state.currentActivity = null;
      state.startActivityError = null;
    },
    setActiveSession(state, action: PayloadAction<ActivitySession>) {
      state.activeSession = action.payload as any;
    },
    clearActiveSession(state) {
      state.activeSession = null;
    },

    // ── Full reset ────────────────────────────────────────────────────────
    resetAllActivityDrafts() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    // ── discardPeopleHubActivity ─────────────────────────────────────────
    builder
      .addCase(discardPeopleHubActivity.fulfilled, (state) => {
        state.currentActivity = null;
        state.activeSession = null;
        state.pulseCheck.activityId = null;
        state.fullObservation.activityId = null;
        state.routineObservation.activityId = null;
      });

    // ── startPeopleHubActivity ───────────────────────────────────────────
    builder
      .addCase(startPeopleHubActivity.pending, (state) => {
        state.isStartingActivity = true;
        state.startActivityError = null;
      })
      .addCase(startPeopleHubActivity.fulfilled, (state, action) => {
        state.isStartingActivity = false;
        state.currentActivity = action.payload;
      })
      .addCase(startPeopleHubActivity.rejected, (state, action) => {
        state.isStartingActivity = false;
        state.startActivityError = action.payload as string;
      });

    // ── resumePeopleHubActivity ──────────────────────────────────────────
    builder
      .addCase(resumePeopleHubActivity.pending, (state) => {
        state.isResumingActivity = true;
        state.resumeActivityError = null;
      })
      .addCase(resumePeopleHubActivity.fulfilled, (state) => {
        state.isResumingActivity = false;
      })
      .addCase(resumePeopleHubActivity.rejected, (state, action) => {
        state.isResumingActivity = false;
        state.resumeActivityError = action.payload as string;
      });

    // ── fetchMetricRows ──────────────────────────────────────────────────
    builder
      .addCase(fetchMetricRows.fulfilled, (state, action) => {
        const { activityType, rows } = action.payload;
        if (activityType === "pulse_check") {
          state.pulseCheck.metricRows = rows;
        } else if (activityType === "full_observation") {
          state.fullObservation.before.metricRows = rows;
          state.fullObservation.during.metricRows = rows;
          state.fullObservation.after.metricRows = rows;
        } else {
          state.routineObservation.before.metricRows = rows;
          state.routineObservation.during.metricRows = rows;
          state.routineObservation.after.metricRows = rows;
        }
      });

    // ── fetchCoachingItems ───────────────────────────────────────────────
    builder
      .addCase(fetchCoachingItems.fulfilled, (state, action) => {
        const { activityType, openItems, historyItems } = action.payload;
        if (activityType === "pulse_check") {
          state.pulseCheck.openItems = openItems;
          state.pulseCheck.historyItems = historyItems;
        } else if (activityType === "full_observation") {
          state.fullObservation.openItems = openItems;
          state.fullObservation.historyItems = historyItems;
        } else {
          state.routineObservation.openItems = openItems;
          state.routineObservation.historyItems = historyItems;
        }
      });

    // ── saveDraft ────────────────────────────────────────────────────────
    builder
      .addCase(saveDraft.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.isSaving = false;
        const { activityType, response } = action.payload;
        const savedAt = response.savedAt;
        const activityId = response.activityId;
        if (activityType === "pulse_check") {
          state.pulseCheck.savedAt = savedAt;
          state.pulseCheck.activityId = activityId;
        } else if (activityType === "full_observation") {
          state.fullObservation.savedAt = savedAt;
          state.fullObservation.activityId = activityId;
        } else {
          state.routineObservation.savedAt = savedAt;
          state.routineObservation.activityId = activityId;
        }
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload as string;
      });

    // ── submitActivity ───────────────────────────────────────────────────
    builder
      .addCase(submitActivity.pending, (state) => {
        state.isSaving = true;
        state.saveError = null;
      })
      .addCase(submitActivity.fulfilled, (state, action) => {
        state.isSaving = false;
        state.successActivityId = action.payload.response.activityId;
        // Clear the draft that was just submitted
        const activityType = action.payload.activityType;
        if (activityType === "pulse_check") {
          state.pulseCheck = { ...pulseCheckDraftInitial };
        } else if (activityType === "full_observation") {
          state.fullObservation = { ...observationDraftInitial };
        } else {
          state.routineObservation = { ...observationDraftInitial };
        }
      })
      .addCase(submitActivity.rejected, (state, action) => {
        state.isSaving = false;
        state.saveError = action.payload as string;
      });
  },
});

export const {
  // pulse check
  setPulseCheckFeedback,
  setPulseCheckActionDraft,
  setPulseCheckSignatures,
  setPulseCheckOpenItemResult,
  setPulseCheckOpenItemFeedback,
  resetPulseCheckDraft,
  // full observation
  setFullObsActiveScreen,
  setFullObsBuildingStatusAnswer,
  setFullObsNotes,
  setFullObsFeedback,
  setFullObsActionDraft,
  setFullObsSignatures,
  setFullObsOpenItemResult,
  setFullObsOpenItemFeedback,
  resetFullObservationDraft,
  // routine observation
  setRoutineObsActiveScreen,
  setRoutineObsBuildingStatusAnswer,
  setRoutineObsNotes,
  setRoutineObsFeedback,
  setRoutineObsActionDraft,
  setRoutineObsSignatures,
  setRoutineObsOpenItemResult,
  setRoutineObsOpenItemFeedback,
  resetRoutineObservationDraft,
  // error / success
  clearSaveError,
  clearSuccessActivityId,
  clearCurrentActivity,
  setActiveSession,
  clearActiveSession,
  // root
  resetAllActivityDrafts,
} = peopleHubActivitySlice.actions;

export default peopleHubActivitySlice.reducer;
